// Shelves' filters (Phase 6.2 Part 3, agreed with Kristi Oct 28; mockup round 2): the groups in
// Kristi's order, what each book offers to them, matching and counts. No database calls here,
// so the rules can be tested on their own (tests/logic/shelf-filters.test.ts).
//
// A book must match every group that has chips on; within a group, any chip (Show only), or
// none of them (Hide). Filters stick on this device (Kristi, Oct 28).

import type { ShelfKey } from './model';
import { COUNT_WORDS, LIB_STATES, STATE_NAMES, loanState, needsYou, needsParts, type LibState, type NeedsKey, type SummaryPart } from '../lib/libraryStates';

type LoanFact = { step: string; due_on: string | null; expected_on?: string | null; check_days?: number | null; quiet_until?: string | null };

/** What a book offers the filters (read once for every book on the Reading tab). */
export interface FilterFacts {
  id: string;
  shelf: ShelfKey | 'reference';
  /** Also on the Finished shelf (a finished read), whatever its shelf now. */
  finished: boolean;
  readType: 'light' | 'absorbing' | 'demanding' | 'study' | 'other' | 'none';
  readListen: 'read' | 'listen' | 'both' | null;
  interest: number | null;
  owned: boolean;
  shared: boolean;
  formats: string[];
  literary: string | null;
  genres: string[];
  subgenres: string[];
  tags: string[];
  /** The library states of your active holds and loans (libraryOf, lib/libraryStates.ts). */
  library: string[];
  addedAt: string;
  /** Added by an import (Recently added keeps imports apart, Kristi Oct 30). */
  imported?: boolean;
  readAgain: boolean;
  tryAgain: boolean;
  favorite: boolean;
}

export type GroupKey =
  | 'readtype'
  | 'readlisten'
  | 'interest'
  | 'copies'
  | 'format'
  | 'literary'
  | 'genre'
  | 'subgenre'
  | 'tags'
  | 'shelf'
  | 'library'
  | 'recent'
  | 'marks'
  // My books › Owned copies (Part 3, Kristi Oct 31: option b, the shared Filters panel).
  | 'where'
  | 'how'
  | 'state'
  | 'ids'
  // My books › Recently added: where a book came from (Kristi, Oct 31; was the Showing menu).
  | 'came_from';

export interface Chip {
  id: string;
  label: string;
  count: number;
  /** A teal dot: what's under this chip needs you (Kristi, Oct 31). */
  needs?: boolean;
  /** A hollow ring: worth a look (filters review F2, Kristi Nov 2); never lights a tab. */
  worth?: boolean;
  /** The dot's or ring's tooltip, naming what's waiting ("Needs you: 2 ready"; R7 N9). */
  tip?: string;
}

/**
 * The panel's groups (filters review F16, Kristi Nov 2): small headings on a laptop and iPad,
 * groups that fold in the phone's sheet. A panel with only a few rows has no headings.
 */
export type Section = 'book' | 'reading' | 'copies' | 'when';
export const SECTIONS: [Section, string][] = [
  ['book', 'About the book'],
  ['reading', 'Your reading'],
  ['copies', 'Copies'],
  ['when', 'Where and when'],
];
/** Headings (and phone folds) only once a panel has this many rows. */
export const SECTION_MIN_ROWS = 6;

export interface Group {
  key: GroupKey;
  /** The row it's drawn in . */
  row: string;
  label: string;
  /** Show only / Hide: rows of kinds only, never rows of conditions (filters review F8). */
  hideable: boolean;
  chips: Chip[];
  /** Its heading in a long panel (F16). */
  section?: Section;
  /** Long lists show the first ones, then "+ N more" (Genre, Tags). */
  firstShown?: number;
  /** The "+ N more" words, when the rest are something else ("+ 4 older", Came from; F15). */
  moreLabel?: (n: number) => string;
  /** Shown instead of chips when there are none (Subgenre before a genre is picked). */
  empty?: string;
}

export type GroupState = { mode: 'show' | 'hide'; on: string[] };
export type FilterState = Partial<Record<GroupKey, GroupState>>;

/**
 * The row catalog in panel order (filters review F6, F16, Kristi Nov 2): About the book, Your
 * reading, Copies, Where and when. Each view picks the rows that suit its list.
 */
export const ROWS = ['Literary genre', 'Genre', 'Subgenre', 'Tags', 'Read type', 'Read mode', 'Interest', 'Marks', 'Copies', 'Format', 'Library', 'Shelf', 'Recently added'] as const;

const SHELF_LABELS: [ShelfKey, string][] = [
  ['currently_reading', 'Currently Reading'],
  ['up_next', 'Up Next'],
  ['future_reads', 'Future reads'],
  ['finished', 'Finished'],
  ['not_for_me', 'Not for me'],
  ['no_status', 'No shelf yet'],
];

const FORMAT_LABELS: Record<string, string> = {
  kindle: 'Kindle', audible: 'Audible', paper: 'Paper', apple_books: 'Apple Books', google_play: 'Google Play', kobo: 'Kobo',
  libro_fm: 'Libro.fm', pdf_epub: 'PDF', article: 'Article', libby: 'Libby', hoopla: 'Hoopla',
};

/** The values a book has for one group. */
export function valuesOf(f: FilterFacts, g: GroupKey, recentFrom: string): string[] {
  switch (g) {
    case 'readtype':
      return [f.readType];
    case 'readlisten':
      return f.readListen ? [f.readListen] : [];
    case 'interest':
      return [f.interest ? String(f.interest) : 'none'];
    case 'copies':
      return [f.owned ? 'owned' : 'not_owned', ...(f.shared ? ['shared'] : [])];
    case 'format':
      return f.formats;
    case 'literary':
      return f.literary ? [f.literary] : [];
    case 'genre':
      return f.genres;
    case 'subgenre':
      return f.subgenres;
    case 'tags':
      return f.tags;
    case 'shelf':
      return f.finished && f.shelf !== 'finished' ? [f.shelf, 'finished'] : [f.shelf];
    case 'library':
      return f.library;
    case 'recent':
      // Books you added (by hand, from Discover, through Claude) apart from imports (Kristi, Oct 30).
      return f.addedAt.slice(0, 10) >= recentFrom ? [f.imported ? 'recent_import' : 'recent'] : [];
    case 'marks':
      return [...(f.readAgain ? ['read_again'] : []), ...(f.tryAgain ? ['try_again'] : []), ...(f.favorite ? ['favorite'] : [])];
    default:
      // Owned copies' own rows (where, how, state, ids) don't apply to books.
      return [];
  }
}

/** Does a book match every group with chips on? */
export function matches(f: FilterFacts, state: FilterState, recentFrom: string): boolean {
  for (const [g, s] of Object.entries(state) as [GroupKey, GroupState][]) {
    if (!s || !s.on.length) continue;
    const vals = valuesOf(f, g, recentFrom);
    const any = s.on.some((c) => vals.includes(c));
    if (s.mode === 'show' ? !any : any) return false;
  }
  return true;
}

/** How many filters are on: one per chip (the Filters (3) count). */
export const filterCount = (state: FilterState) => Object.values(state).reduce((n, s) => n + (s?.on.length ?? 0), 0);

/** The chips that are on, for the header: "Demanding", "Not owned yet", a hidden "Mystery" struck through. */
export function activeChips(groups: Group[], state: FilterState): { group: GroupKey; id: string; label: string; hide: boolean }[] {
  const out: { group: GroupKey; id: string; label: string; hide: boolean }[] = [];
  for (const g of groups) {
    const s = state[g.key];
    if (!s) continue;
    for (const id of s.on) {
      const c = g.chips.find((x) => x.id === id);
      out.push({ group: g.key, id, label: c?.label ?? id, hide: s.mode === 'hide' });
    }
  }
  return out;
}

/** Turn a chip on or off (keeping its group's mode). */
export function toggleChip(state: FilterState, g: GroupKey, id: string): FilterState {
  const s = state[g] ?? { mode: 'show', on: [] };
  const on = s.on.includes(id) ? s.on.filter((x) => x !== id) : [...s.on, id];
  const next = { ...state, [g]: { ...s, on } };
  // Subgenres follow the genres picked: taking a genre off drops its subgenres (done by the page).
  return next;
}

export function setMode(state: FilterState, g: GroupKey, mode: 'show' | 'hide'): FilterState {
  const s = state[g] ?? { mode: 'show', on: [] };
  return { ...state, [g]: { ...s, mode } };
}

/** The lists the groups are built from. */
export interface FilterLists {
  readTypes: { name: string }[];
  genres: { id: string; name: string; short: string | null; hidden: boolean }[];
  subgenres: { id: string; name: string; genreId: string }[];
  tags: { id: string; name: string }[];
  literaryOn: string[];
  recentDays: number;
}

/** The state without one group: what that group's chips are counted within (F10). */
export function withoutGroup<K extends string, S>(state: Partial<Record<K, S>>, g: K): Partial<Record<K, S>> {
  const { [g]: _drop, ...rest } = state as Record<K, S>;
  return rest as Partial<Record<K, S>>;
}

/**
 * Keep the chips with books, and any chip that's on so it can be turned off (filters review F10,
 * Kristi Nov 2: a chip at [0] is hidden unless it's on, in every panel).
 */
export function keepChips<C extends { id: string; count: number }>(chips: C[], on: readonly string[] = []): C[] {
  return chips.filter((c) => c.count > 0 || on.includes(c.id));
}

/**
 * The rows in the catalog's order (F6, F16), each chip counted within what the other rows leave
 * (F10, Kristi Nov 2), so a count is always what tapping it would show. Pass the view's books
 * already narrowed by its scope and quick chips.
 */
export function buildGroups(facts: FilterFacts[], lists: FilterLists, state: FilterState, recentFrom: string): Group[] {
  const within = new Map<GroupKey, FilterFacts[]>();
  const pool = (g: GroupKey) => {
    let list = within.get(g);
    if (!list) {
      const rest = withoutGroup(state, g);
      list = Object.keys(rest).length ? facts.filter((f) => matches(f, rest, recentFrom)) : facts;
      within.set(g, list);
    }
    return list;
  };
  const chip = (g: GroupKey, id: string, label: string): Chip => ({ id, label, count: pool(g).filter((f) => valuesOf(f, g, recentFrom).includes(id)).length });
  const keep = (g: GroupKey, chips: Chip[]) => keepChips(chips, state[g]?.on);
  const pickedGenres = state.genre?.mode === 'show' ? state.genre.on : [];
  const out: Group[] = [
    {
      key: 'literary',
      row: 'Literary genre',
      label: 'Literary genre',
      hideable: true,
      section: 'book',
      chips: keep('literary', (['fiction', 'nonfiction', 'poetry', 'drama'] as const).map((k) => chip('literary', k, k[0]!.toUpperCase() + k.slice(1)))),
    },
    { key: 'genre', row: 'Genre', label: 'Genre', hideable: true, section: 'book', firstShown: 12, chips: keep('genre', lists.genres.filter((g) => !g.hidden).map((g) => chip('genre', g.id, g.short ?? g.name))) },
    {
      key: 'subgenre',
      row: 'Subgenre',
      label: 'Subgenre',
      hideable: true,
      section: 'book',
      firstShown: 16,
      chips: keep('subgenre', lists.subgenres.filter((s) => pickedGenres.includes(s.genreId)).map((s) => chip('subgenre', s.id, s.name))),
      empty: pickedGenres.length ? 'No subgenres under the genres you picked.' : 'Pick a genre under Genre to narrow it by subgenre.',
    },
    { key: 'tags', row: 'Tags', label: 'Tags', hideable: true, section: 'book', firstShown: 12, chips: keep('tags', lists.tags.map((t) => chip('tags', t.id, t.name))) },
    {
      key: 'readtype',
      row: 'Read type',
      label: 'Read type',
      hideable: false,
      section: 'reading',
      chips: keep('readtype', [
        chip('readtype', 'light', 'Light'),
        chip('readtype', 'absorbing', 'Absorbing'),
        chip('readtype', 'demanding', 'Demanding'),
        chip('readtype', 'study', 'Study'),
        chip('readtype', 'other', 'Other read types'),
        chip('readtype', 'none', 'No read type'),
      ]),
    },
    { key: 'readlisten', row: 'Read mode', label: 'Read mode', hideable: false, section: 'reading', chips: keep('readlisten', [chip('readlisten', 'read', 'Read'), chip('readlisten', 'listen', 'Listen'), chip('readlisten', 'both', 'Read & Listen')]) },
    { key: 'interest', row: 'Interest', label: 'Interest', hideable: false, section: 'reading', chips: keep('interest', ['5', '4', '3', '2', '1'].map((n) => chip('interest', n, n)).concat(chip('interest', 'none', 'Interest not set'))) },
    { key: 'marks', row: 'Marks', label: 'Marks', hideable: false, section: 'reading', chips: keep('marks', [chip('marks', 'read_again', '↻ Read again'), chip('marks', 'try_again', '↺ Try again'), chip('marks', 'favorite', '♥ Favorites')]) },
    // "Missing details" left Copies (Kristi, Nov 2): the ID is fixed in Owned copies and Update details.
    { key: 'copies', row: 'Copies', label: 'Copies', hideable: false, section: 'copies', chips: keep('copies', [chip('copies', 'owned', 'Owned'), chip('copies', 'not_owned', 'Not owned yet'), chip('copies', 'shared', 'Shared with me')]) },
    {
      key: 'format',
      row: 'Format',
      label: 'Format',
      hideable: true,
      section: 'copies',
      chips: keep('format', Object.entries(FORMAT_LABELS).map(([id, label]) => chip('format', id, label))).sort((a, b) => b.count - a.count),
    },
    {
      key: 'library',
      row: 'Library',
      label: 'Library',
      hideable: false,
      section: 'copies',
      // The library states in their one order and words (R2, L1); a chip that needs you has the dot.
      chips: keep(
        'library',
        LIB_STATES.filter((s) => s !== 'to_place').map((s) => {
          const c = chip('library', s, STATE_NAMES[s]);
          return needsYou(s) && c.count ? { ...c, needs: true, tip: `Needs you: ${COUNT_WORDS[s](c.count)}` } : c;
        }),
      ),
    },
    { key: 'shelf', row: 'Shelf', label: 'Shelf', hideable: true, section: 'when', chips: keep('shelf', SHELF_LABELS.map(([k, l]) => chip('shelf', k, l))) },
    {
      key: 'recent',
      row: 'Recently added',
      label: 'Recently added',
      hideable: false,
      section: 'when',
      chips: keep('recent', [chip('recent', 'recent', `Added in the last ${lists.recentDays} days`), chip('recent', 'recent_import', `Imported in the last ${lists.recentDays} days`)]),
    },
  ];
  return out;
}

/** Keep subgenre chips only under genres still picked (a genre taken off takes its subgenres with it). */
export function pruneSubgenres(state: FilterState, lists: FilterLists): FilterState {
  const s = state.subgenre;
  if (!s?.on.length) return state;
  const picked = state.genre?.mode === 'show' ? state.genre.on : [];
  const keep = s.on.filter((id) => picked.includes(lists.subgenres.find((x) => x.id === id)?.genreId ?? ''));
  return keep.length === s.on.length ? state : { ...state, subgenre: { ...s, on: keep } };
}

/** Recently added's first day: today less N days. */
export function recentFrom(today: string, days: number): string {
  const d = new Date(`${today}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

/**
 * A book's Library values (consistency pass R2, Kristi Nov 2): the state of each of its active
 * holds and loans, from the one list in lib/libraryStates.ts (loan ended, ready, due soon, check
 * Libby, not started, on loan, on hold), so Shelves and My books › Library copies classify the
 * same loans the same way. Check Libby uses each library's own window (check_days_before).
 */
export function libraryOf(loans: LoanFact[], today: string, reading = false): string[] {
  const out = new Set<string>();
  for (const l of loans) {
    const s = loanState({ step: l.step, dueOn: l.due_on, expectedOn: l.expected_on ?? null, checkDays: l.check_days, quietUntil: l.quiet_until ?? null }, reading, today);
    if (s) out.add(s);
  }
  return LIB_STATES.filter((s) => out.has(s));
}

/** The alert line's parts are the needs-you states, most urgent first (L1). */
export type AlertKey = NeedsKey;

/** What a part says when its books are showing ("Showing books ready to borrow from the library line"). */
export const ALERT_SHOWING: Record<AlertKey, string> = {
  loan_ended: 'loans that ended',
  ready: 'books ready to borrow',
  due_soon: 'loans due soon',
  check_libby: 'holds to check in Libby',
  not_started: 'loans not started',
};

/** A book's state on the alert line: its most urgent Library value. */
export const stateOfFacts = (f: Pick<FilterFacts, 'library'>): LibState | null => (LIB_STATES.find((s) => f.library.includes(s)) ?? null);

/**
 * The library alert line (L2): each book counted once, under its most urgent part (a book with two
 * loans, as The Redbreast's Kindle and Libby audio, is one book). Empty when nothing needs you.
 */
export function libraryAlert(facts: Pick<FilterFacts, 'library'>[]): SummaryPart[] {
  return needsParts(facts.map(stateOfFacts));
}

/** Read or listen from a book's copies you have: an audiobook kind or audio app counts as Listen. */
export function readListenOf(formats: string[]): FilterFacts['readListen'] {
  if (!formats.length) return null;
  const listen = formats.some((f) => f === 'audible' || f === 'libro_fm');
  const read = formats.some((f) => f !== 'audible' && f !== 'libro_fm');
  return listen && read ? 'both' : listen ? 'listen' : 'read';
}

/** Up Next with the Library filter on (decisions doc, Oct 22): ready, then borrowed by due date, then holds by expected date. */
export function libraryOrder<T extends { loan?: { step: string; due_on: string | null; expected_on: string | null } }>(books: T[]): T[] {
  const rank = (b: T) => (b.loan?.step === 'ready' ? 0 : b.loan?.step === 'borrowed' ? 1 : b.loan?.step === 'on_hold' ? 2 : 3);
  const when = (b: T) => (b.loan?.step === 'borrowed' ? b.loan.due_on : b.loan?.expected_on) ?? '9999';
  return [...books].sort((a, b) => rank(a) - rank(b) || when(a).localeCompare(when(b)));
}

/**
 * The Reference tab's rows (Kristi, Oct 28), in the catalog's order (F6): Tags, ♥ Favorites, Copies,
 * Format, Recently added.
 * Genre and subgenre are picked in the views themselves instead: the genre panes and Jump to,
 * the A–Z genre chips, each pane's subgenre chips (Kristi, Oct 29).
 */
export const REFERENCE_KEYS: GroupKey[] = ['tags', 'marks', 'copies', 'format', 'recent'];

export function referenceGroups(groups: Group[]): Group[] {
  return REFERENCE_KEYS.flatMap((k) => groups.filter((g) => g.key === k))
    .map((g) => (g.key === 'marks' ? { ...g, chips: g.chips.filter((c) => c.id === 'favorite') } : g))
    .filter((g) => g.chips.length > 0); // a row with nothing to pick doesn't show
}
